import type { ReactNode, MouseEvent } from "react";

/**
 * Beyond Sure — Chip primitive (P0).
 *
 * The small pill used across the product: status badges (Active / Audit
 * pending / Expiring soon), coverage chips on policy cards, filter chips,
 * tier badges (NEW / Pro / Enterprise), quick-action chips on the composer.
 *
 * Five tones map to brand hierarchy:
 *   brand    : Spring wash + deep ink — default highlight
 *   muted    : Ink-08 bg + ink-60 — for secondary tags
 *   solid    : Brand-deep filled — for primary status (Active)
 *   warn     : Soft red — for attention (Audit pending)
 *   amber    : Soft amber — for caution (Expiring soon)
 *
 * `interactive` flag adds hover ring + cursor for filter/select use.
 * `onRemove` adds a × button (useful for selected filter chips).
 */
type Tone = "brand" | "muted" | "solid" | "warn" | "amber" | "info";

interface ChipProps {
  children: ReactNode;
  tone?: Tone;
  size?: "xs" | "sm" | "md";
  interactive?: boolean;
  onClick?: (e: MouseEvent<HTMLSpanElement | HTMLButtonElement>) => void;
  onRemove?: () => void;
  leading?: ReactNode;
  "data-testid"?: string;
  className?: string;
}

export function Chip({
  children,
  tone = "muted",
  size = "sm",
  interactive = false,
  onClick,
  onRemove,
  leading,
  className,
  ...rest
}: ChipProps) {
  const palette =
    tone === "brand"
      ? "bg-[var(--color-brand-soft)] text-[var(--color-brand-deep)]"
      : tone === "solid"
      ? "bg-[var(--color-brand-deep)] text-[var(--color-paper)]"
      : tone === "warn"
      ? "bg-[rgb(214_64_64_/_0.12)] text-[#9a3a3a]"
      : tone === "amber"
      ? "bg-[rgb(199_132_17_/_0.14)] text-[#8a6a18]"
      : tone === "info"
      ? "bg-[rgb(30_144_255_/_0.12)] text-[var(--color-azure)]"
      : "bg-[var(--color-ink-08)] text-[var(--color-ink-60)]";

  const sizeClass =
    size === "xs"
      ? "h-5 px-2 text-[9.5px] font-semibold uppercase tracking-[0.12em]"
      : size === "md"
      ? "h-7 px-3 text-[12px] font-medium"
      : "h-6 px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.12em]";

  const baseClass = [
    "inline-flex items-center gap-1 rounded-full whitespace-nowrap",
    "transition-colors duration-[var(--duration-fast)]",
    sizeClass,
    palette,
    interactive
      ? "cursor-pointer hover:ring-2 hover:ring-[var(--color-brand)]/30"
      : "",
    className ?? "",
  ].join(" ");

  if (onClick && interactive) {
    return (
      <button type="button" onClick={onClick} className={baseClass} {...rest}>
        {leading}
        <span>{children}</span>
        {onRemove ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Remove"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="ml-0.5 -mr-0.5 hover:opacity-70"
          >
            ×
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <span className={baseClass} {...rest}>
      {leading}
      <span>{children}</span>
      {onRemove ? (
        <button
          type="button"
          aria-label="Remove"
          onClick={onRemove}
          className="ml-0.5 -mr-0.5 hover:opacity-70 cursor-pointer"
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

export default Chip;
